import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { qualityDefectSchema, type QualityDefectFormData } from "@/schemas/qualitySchemas";

interface DefectFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inspectionId: string;
}

export const DefectFormDialog = ({ open, onOpenChange, inspectionId }: DefectFormDialogProps) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<QualityDefectFormData>({
    resolver: zodResolver(qualityDefectSchema),
    defaultValues: {
      defect_type: "visual",
      severity: "minor",
      description: "",
      location: "",
      quantity: 1,
      corrective_action: "",
    },
  });

  const createDefect = useMutation({
    mutationFn: async (data: QualityDefectFormData) => {
      const defectData = {
        inspection_id: inspectionId,
        defect_type: data.defect_type,
        severity: data.severity,
        description: data.description,
        location: data.location,
        quantity: data.quantity,
        corrective_action: data.corrective_action,
      };

      const { error } = await api.from("quality_defects").insert([defectData]);

      if (error) throw error;

      // Update defects count
      const { data: defects } = await api
        .from("quality_defects")
        .select("quantity")
        .eq("inspection_id", inspectionId);

      const totalDefects = defects?.reduce((sum, d) => sum + (d.quantity || 1), 0) || 0;

      await api
        .from("quality_inspections")
        .update({ defects_found: totalDefects })
        .eq("id", inspectionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quality-defects", inspectionId] });
      queryClient.invalidateQueries({ queryKey: ["quality-inspections"] });
      toast.success("Defecto registrado exitosamente");
      form.reset();
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error("Error al registrar el defecto: " + error.message);
    },
  });

  const onSubmit = async (data: QualityDefectFormData) => {
    setIsSubmitting(true);
    try {
      await createDefect.mutateAsync(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Registrar Defecto</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="defect_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Defecto *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona el tipo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="visual">Visual</SelectItem>
                        <SelectItem value="dimensional">Dimensional</SelectItem>
                        <SelectItem value="structural">Estructural</SelectItem>
                        <SelectItem value="functional">Funcional</SelectItem>
                        <SelectItem value="cosmetic">Cosmético</SelectItem>
                        <SelectItem value="other">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="severity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Severidad *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona la severidad" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="critical">Crítico</SelectItem>
                        <SelectItem value="major">Mayor</SelectItem>
                        <SelectItem value="minor">Menor</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción *</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} placeholder="Describe el defecto en detalle..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ubicación</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Sección superior izquierda" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cantidad</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} onChange={(e) => field.onChange(parseInt(e.target.value))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="corrective_action"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Acción Correctiva</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} placeholder="Describe la acción correctiva propuesta..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-3">
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? "Registrando..." : "Registrar Defecto"}
              </Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
